import React, { useContext, useEffect, useRef, useState } from 'react';
import './index.scss';
import TextField from '../../UI/TextField';
import FieldGroup from '../../UI/FieldGroup';
import SelectField, { ISelectFieldProps } from '../../UI/SelectField';
import Button from '../../UI/Button';
import useRegion from '../../../hooks/useRegion';
import { JSDCContext } from '../../../JSDC/Context';
import { DuiContext } from '../../Context';
import CheckBoxDropDownField, {
  ICheckBoxesFieldProps,
  CheckBoxesValues,
} from '../../UI/CheckBoxesField';
import { TrailQueryApi } from '../../../apiConfigs/@types';

export interface ITreeSearchDialogContentProps {}

const TreeSearchDialogContent: React.FC<
  ITreeSearchDialogContentProps
> = ({}: ITreeSearchDialogContentProps) => {
  const { trailQuery, advanceListingQuery, Jsdc, trailCreateEvent, advanceCreateEvent } =
    useContext(JSDCContext);
  const dui = useContext(DuiContext);
  const { counties: _counties, getTownsByCounty } = useRegion();
  const [id, setId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [reporter, setReporter] = useState('');
  const [basinName, setBasinName] = useState('');
  const [listingCategory, setListingCategory] = useState('');
  const [county, setCounty] = useState('');
  const [town, setTown] = useState('');
  const counties = _counties.map(({ countyname, countycode }) => ({
    name: countyname,
    value: countycode,
  }));
  const [towns, setTowns] = useState<ISelectFieldProps['options']>([]);
  const [processingStatus, setProcessing] = useState('');
  const processingStatusOptions: ISelectFieldProps['options'] = [
    { name: '未處理', value: '未處理' },
    { name: '已處理', value: '已處理' },
    { name: '已通報主管機關', value: '已通報主管機關' },
    { name: '不建議/無法處理', value: '不建議/無法處理' },
  ];
  const listingCategoryOptions: ISelectFieldProps['options'] = [
    { name: '工程擾動', value: '工程擾動' },
    { name: '水質污染', value: '水質污染' },
    { name: '河川構造物', value: '河川構造物' },
    { name: '河溪樣貌', value: '河溪樣貌' },
    { name: '其它事件', value: '其它事件' },
  ];
  const [isLoading, setIsLoading] = useState(false);
  const [showOutcomes, setShowOutcomes] = useState(true);
  const [reportStartDate, setReportStartDate] = useState<string>('');
  const [reportEndDate, setReportEndDate] = useState<string>('');
  const [occurrence, setOccurrence] = useState<CheckBoxesValues>([
    { value: false, name: '步道本體損耗/退化' },
    { value: false, name: '工程/人為破壞' },
    { value: false, name: '設施物損壞' },
    { value: false, name: '通阻回報' },
    { value: false, name: '其他' },
  ]);
  const validToSearch = () => {
    const atLeastOneInput = !!(
      id ||
      keyword ||
      reporter ||
      basinName ||
      listingCategory ||
      county ||
      town ||
      processingStatus ||
      reportStartDate ||
      reportEndDate ||
      occurrence.some(({ value }) => value)
    );
    return atLeastOneInput;
  };

  const handleCountySelect = async (countyCode: string) => {
    setTown('');
    const towns = await getTownsByCounty({ countyCode });
    setCounty(countyCode);
    setTowns(
      towns?.map(({ towncode, townname }) => ({
        name: townname,
        value: towncode,
      })) || [],
    );
  };

  const isVaildDate = (start: string, end: string) => {
    if (!start && !end) {
      return true;
    } else if (start && !end) {
      alert('請輸入結束日期');
      return false;
    } else if (!start && end) {
      alert('請輸入起始日期');
      return false;
    } else if (new Date(start) > new Date(end)) {
      alert('回報日期範圍錯誤：結束日期大於起始日期。請重新輸入');
      return false;
    }
    return true;
  };

  // 把目前查詢條件寫回 URL query（雙向同步，方便分享/重整保留）
  const writeUrlFromState = () => {
    const p = new URLSearchParams();
    if (id) p.set('id', id);
    if (basinName) p.set('basin_name', basinName);
    if (keyword) p.set('keyword', keyword);
    if (reporter) p.set('occurrence', reporter);
    if (listingCategory) p.set('listing_category', listingCategory);
    if (processingStatus) p.set('processing_status', processingStatus);
    if (county) p.set('county', county);
    if (town) p.set('town', town);
    if (reportStartDate) p.set('report_start_date', reportStartDate);
    if (reportEndDate) p.set('report_end_date', reportEndDate);
    const qs = p.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  };

  // 執行一次搜尋（清空圖層 → 查詢 → flyToBounds）。不依賴 state，供 handleSubmit 與 URL 帶入共用。
  const runSearch = async (
    queryOptions: TrailQueryApi['Request'],
    start: string,
    end: string,
    occurrences: string[],
  ) => {
    trailQuery.clearTrails();
    advanceListingQuery.clearAdvanceListing();
    setIsLoading(true);

    const [trailsLayer, advanceLayer] = await Promise.all([
      trailQuery.queryTrails(queryOptions, start, end, occurrences),
      advanceListingQuery.queryAdvance(queryOptions, start, end, occurrences),
    ]);
    const viewer = await Jsdc.asyncViewer;
    const trailHasLayers = (trailsLayer.instance?.getLayers().length ?? 0) > 0;
    const advanceHasLayers = (advanceLayer.instance?.getLayers().length ?? 0) > 0;
    if (trailHasLayers && advanceHasLayers && trailsLayer.instance && advanceLayer.instance) {
      const bounds = trailsLayer.instance.getBounds().extend(advanceLayer.instance.getBounds());
      viewer.flyToBounds(bounds);
    } else if (trailHasLayers && trailsLayer.instance) {
      viewer.flyToBounds(trailsLayer.instance.getBounds());
    } else if (advanceHasLayers && advanceLayer.instance) {
      viewer.flyToBounds(advanceLayer.instance.getBounds());
    }
    setIsLoading(false);
    setShowOutcomes(true);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!isVaildDate(reportStartDate, reportEndDate)) {
      return;
    }

    const queryOptions: TrailQueryApi['Request'] = {
      id,
      keyword,
      county,
      town,
      processing_status: processingStatus,
      basin_name: basinName,
      listing_category: listingCategory,
      occurrence: reporter,
    };
    const selectedOccurrences = occurrence.filter(({ value }) => value).map(({ name }) => name);

    writeUrlFromState();
    await runSearch(queryOptions, reportStartDate, reportEndDate, selectedOccurrences);
  };

  const handleClear = async () => {
    trailQuery.clearTrails();
    advanceListingQuery.clearAdvanceListing();
    // 清除後回到顯示全部：重新載入所有點位到地圖（同時 raise create 事件，
    // 讓地圖圖層勾選與環境紀錄列表回到全部並保持連動）
    await Promise.all([trailQuery.getAllTrails(), advanceListingQuery.getAll()]);
    setId('');
    setKeyword('');
    setReporter('');
    setBasinName('');
    setListingCategory('');
    setCounty('');
    setTown('');
    setProcessing('');
    setReportStartDate('');
    setReportEndDate('');
    setOccurrence([
      { value: false, name: '步道本體損耗/退化' },
      { value: false, name: '工程/人為破壞' },
      { value: false, name: '設施物損壞' },
      { value: false, name: '通阻回報' },
      { value: false, name: '其他' },
    ]);
    setShowOutcomes(false);
    // 清除 URL query
    window.history.replaceState(null, '', window.location.pathname);
  };

  const treesCount =
    (trailQuery.trailsLayer.instance?.getLayers().length ?? 0) +
    (advanceListingQuery.advanceListingLayer.instance?.getLayers().length ?? 0);

  // 載入時：讀 URL query → 回填表單 → 執行搜尋 → 自動展開「環境紀錄」面板。
  // 時序：初始 getAllTrails/getAll 在祖先 JSDCProvider 的 effect（晚於本子元件 effect）才觸發，
  // 若直接搜尋，較慢完成的「載入全部」會 addFeature 覆蓋結果。改為先訂閱既有的
  // trailCreateEvent/advanceCreateEvent，等初始載入的兩個 create 事件都到齊後再跑 URL 搜尋。
  const didUrlInit = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const getVal = (k: string) => params.get(k) ?? '';
    const urlId = getVal('id');
    const urlKeyword = getVal('keyword');
    const urlReporter = getVal('occurrence');
    const urlBasin = getVal('basin_name');
    const urlCategory = getVal('listing_category');
    const urlProcessing = getVal('processing_status');
    const urlCounty = getVal('county');
    const urlTown = getVal('town');
    const urlStart = getVal('report_start_date');
    const urlEnd = getVal('report_end_date');

    const hasQuery = !!(
      urlId ||
      urlKeyword ||
      urlReporter ||
      urlBasin ||
      urlCategory ||
      urlProcessing ||
      urlCounty ||
      urlTown ||
      urlStart ||
      urlEnd
    );
    if (!hasQuery) return;

    let trailReady = false;
    let advanceReady = false;

    const runFromUrl = async () => {
      if (didUrlInit.current) return;
      if (!(trailReady && advanceReady)) return;
      didUrlInit.current = true;
      offTrail();
      offAdvance();

      // 回填表單（供開啟「河溪搜尋」時顯示）
      setId(urlId);
      setKeyword(urlKeyword);
      setReporter(urlReporter);
      setBasinName(urlBasin);
      setListingCategory(urlCategory);
      setProcessing(urlProcessing);
      setReportStartDate(urlStart);
      setReportEndDate(urlEnd);
      if (urlCounty) {
        await handleCountySelect(urlCounty);
        if (urlTown) setTown(urlTown);
      }

      const queryOptions: TrailQueryApi['Request'] = {
        id: urlId,
        keyword: urlKeyword,
        county: urlCounty,
        town: urlTown,
        processing_status: urlProcessing,
        basin_name: urlBasin,
        listing_category: urlCategory,
        occurrence: urlReporter,
      };
      await runSearch(queryOptions, urlStart, urlEnd, []);
      dui.menuSwitch('環境紀錄');
    };

    const offTrail = trailCreateEvent.addEventListener(() => {
      trailReady = true;
      runFromUrl();
    });
    const offAdvance = advanceCreateEvent.addEventListener(() => {
      advanceReady = true;
      runFromUrl();
    });
    return () => {
      offTrail();
      offAdvance();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dui-TreeSearchDialogContent">
      <form className="dui-TreeSearchDialogContent-form" onSubmit={handleSubmit}>
        <TextField
          disabled={isLoading}
          label="回報名稱"
          placeholder="搜尋範例：臺南市急水溪"
          value={id}
          onChange={(e) => setId(e.target.value)}
        />
        <TextField
          disabled={isLoading}
          label="流域名稱"
          placeholder="搜尋範例：花蓮溪"
          value={basinName}
          onChange={(e) => setBasinName(e.target.value)}
        />
        <TextField
          disabled={isLoading}
          label="關鍵字"
          placeholder="搜尋範例：工程"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <TextField
          disabled={isLoading}
          label="回報人員"
          placeholder="搜尋範例：王小明"
          value={reporter}
          onChange={(e) => setReporter(e.target.value)}
        />
        <SelectField
          disabled={isLoading}
          label="環境紀錄類型"
          placeholder="未設定"
          value={listingCategory}
          options={listingCategoryOptions}
          onChange={(e) => setListingCategory(String(e.target.value))}
        />
        {/* <SelectField
          label="步道處理狀況"
          disabled={isLoading}
          placeholder="未設定"
          value={processingStatus}
          onChange={(e) => setProcessing(String(e.target.value))}
          options={processingStatusOptions}
        />
        <FieldGroup label="原因篩選">
          <CheckBoxDropDownField
            disabled={isLoading}
            label="回報原因"
            values={occurrence}
            onChange={setOccurrence}
          />
        </FieldGroup> */}
        <FieldGroup label="行政區">
          <SelectField
            disabled={isLoading}
            placeholder="未設定縣市"
            value={county}
            options={counties}
            onChange={(e) => handleCountySelect(String(e.target.value))}
          />
          <SelectField
            disabled={isLoading}
            placeholder="未設定鄉鎮"
            value={town}
            options={towns}
            onChange={(e) => setTown(String(e.target.value))}
          />
        </FieldGroup>
        <FieldGroup label="回報日期">
          <TextField
            disabled={isLoading}
            type="date"
            value={reportStartDate}
            onChange={(e) => {
              setReportStartDate(e.target.value);
            }}
          />
          <span className="dui-TreeSearchDialogContent-span">到</span>
          <TextField
            disabled={isLoading}
            type="date"
            value={reportEndDate}
            onChange={(e) => {
              setReportEndDate(e.target.value);
            }}
          />
        </FieldGroup>
        <Button disabled={!validToSearch() || isLoading}>搜尋{isLoading && ' loading...'}</Button>
        <Button type="button" onClick={handleClear}>
          清除
        </Button>
      </form>

      {showOutcomes ? (
        <div className="dui-TreeSearchDialogContent-result">
          <span className="dui-TreeSearchDialogContent-result-title">檢索結果</span>
          <span className="dui-TreeSearchDialogContent-result-number">{treesCount}</span>
        </div>
      ) : null}
    </div>
  );
};
TreeSearchDialogContent.displayName = 'TreeSearchDialogContent';
export default TreeSearchDialogContent;
